import { Box, Button, ButtonExtendedProps, Layer, Spinner, Text } from 'grommet';
import { IconProps, Trash } from 'grommet-icons';
import { type JSX, useEffect, useState } from 'react';

export type IDeleteProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick: (e?: React.SyntheticEvent) => Promise<any> | void;
  name: string;
  size?: IconProps['size'];
  /** For actions other than deleting (e.g. archiving): the button's icon, confirm button and question.
   * `null` for no icon, on a labelled button */
  icon?: JSX.Element | null;
  confirmLabel?: string;
  message?: React.ReactNode;
} & Omit<ButtonExtendedProps, 'icon'>;

/** Red is for the confirm button and danger text only; the resting icon is neutral */
export const Delete = ({ onClick, name, size, icon, confirmLabel = 'Delete', message, ...rest }: IDeleteProps) => {
  // Deleting (the default) gets a red confirm button; other uses (adding a storage pack) don't
  const danger = confirmLabel === 'Delete';
  const [open, setOpen] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);

  useEffect(() => {
    const doDelete = async () => {
      await onClick();
      setDeleting(false);
      setOpen(false);
    };
    if (open && deleting) doDelete();
  }, [deleting]);

  const onCancel = () => setOpen(false);

  const acceptFocus = (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent> | React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
  };

  return (
    <>
      <Button
        hoverIndicator
        onClick={(e) => acceptFocus(e)}
        style={{ cursor: 'pointer' }}
        {...rest}
        icon={icon === undefined ? <Trash color="text-secondary" size={size} /> : (icon ?? undefined)}
      />

      {open && (
        <Layer onClickOutside={onCancel} onEsc={onCancel}>
          {deleting ? (
            <Box align="center" justify="center" pad="medium">
              <Spinner size="large" />
            </Box>
          ) : (
            <Box pad="medium" gap="medium">
              {message ?? <Text>Are you sure you want to delete {name}?</Text>}
              <Box direction="row" align="center" justify="end" gap="small">
                <Button label="Cancel" onClick={onCancel} />
                <Button
                  label={confirmLabel}
                  onClick={() => setDeleting(true)}
                  primary
                  color={danger ? 'text-danger' : undefined}
                />
              </Box>
            </Box>
          )}
        </Layer>
      )}
    </>
  );
};
